import { writable } from "svelte/store";
import { Sound } from "../SoundCard";
import { openDB, type IDBPDatabase } from 'idb';
import { userSettings } from "../store";
import { UserSettingIds } from "./UserSettings";

const DB_NAME: string = "bt_sound_db";
const DB_VERSION: number = 2;
const STORE_NAME: string = `bt_sound_store_${DB_VERSION}`;

enum SoundCacheLayer {
    PASSTHROUGH = "PASSTHROUGH",
    IDB = "IDB",
    MEMORY = "MEMORY",
    // FS_API = "FS_API",
}

let cacheEnabled = true;
userSettings.subscribe((settings) => {
    cacheEnabled = settings.getValue(UserSettingIds.SoundCacheEnabled)
})

// const DEFAULT_WEB_AUDIO_MAX_BLOB_SIZE: number = 25 * 1024 * 1024; // 25MB
const DEFAULT_WEB_AUDIO_MAX_BLOB_SIZE: number = 25 * 1024 * 1024; // 25MB
const DEFAULT_MAX_PARALLEL_FETCHES: number = 3;
const DEFAULT_FETCH_CHECK_INTERVAL_MS: number = 500;
const createDefaultConfiguration: () => SoundCacheConfiguration = () => {
    let config = SoundCacheConfiguration.passthrough();
    // if ("storage" in navigator && "getDirectory" in navigator.storage) {
    //     config = config
    //         .withReadFirst(SoundCacheLayer.FS_API)
    //         .withWrite(SoundCacheLayer.FS_API)
    //         .withMaxBlobSize(1024 * 1024 * 1024 * 1024); // 1GB
    // } else {
    // }
    if (cacheEnabled) {
        config = config
            .withReadFirst(SoundCacheLayer.IDB)
            .withWrite(SoundCacheLayer.IDB)
            .withMaxBlobSize(1024 * 1024 * 1024 * 1024); // 1GB
    }
    return config;
}

export class SoundCacheConfiguration {
    public read: SoundCacheLayer[];
    public write: SoundCacheLayer[];
    public maxBlobSize: number;
    public maxParallelFetches: number;

    private constructor(read: SoundCacheLayer[], write: SoundCacheLayer[], maxBlobSize: number, maxParallelFetches: number) {
        this.read = read;
        this.write = write;
        this.maxBlobSize = maxBlobSize;
        this.maxParallelFetches = maxParallelFetches;
    }

    private clone(): SoundCacheConfiguration {
        const clone = structuredClone(this);
        return new SoundCacheConfiguration(clone.read, clone.write, clone.maxBlobSize, clone.maxParallelFetches);
    }

    public static passthrough() {
        return new SoundCacheConfiguration([SoundCacheLayer.PASSTHROUGH], [], DEFAULT_WEB_AUDIO_MAX_BLOB_SIZE, DEFAULT_MAX_PARALLEL_FETCHES);
    }

    public withWrite(writeLayer: SoundCacheLayer): SoundCacheConfiguration {
        const other = this.clone();
        other.write.push(writeLayer)
        return other
    }

    public withReadFirst(readLayer: SoundCacheLayer): SoundCacheConfiguration {
        const other = this.clone();
        other.read.unshift(readLayer)
        return other
    }

    public withMaxBlobSize(maxBlobSize: number): SoundCacheConfiguration {
        const other = this.clone();
        other.maxBlobSize = maxBlobSize;
        return other
    }

    public withMaxParallelFetches(maxParallelFetches: number): SoundCacheConfiguration {
        const other = this.clone();
        other.maxParallelFetches = maxParallelFetches;
        return other
    }
}

export class SoundCache {
    inMemorySounds: Map<string, Blob> = new Map();

    db: IDBPDatabase;
    configuration: SoundCacheConfiguration;
    // fsRoot: FileSystemDirectoryHandle;

    // TODO BL: Move into their own class
    fetchQueue: Sound[];
    fetchSemaphore: number; // Make sure we're never fetching over the limit.
    checkSemaphore: number; // Make sure we're never checking more than once at a time, to avoid two functions scheduling fetches.

    // constructor(db: IDBPDatabase, fsRoot: FileSystemDirectoryHandle) {
    constructor(db: IDBPDatabase) {
        this.db = db;
        // this.fsRoot = fsRoot;
        this.configuration = createDefaultConfiguration();
        console.log("BT: Cache configuration is ");
        console.table(this.configuration);

        this.fetchQueue = [];
        this.fetchSemaphore = 0;
        this.checkSemaphore = 0;
        setInterval(() => {
            if (this.checkSemaphore > 0) {
                // Someone is already checking
                return;
            }
            this.checkSemaphore += 1;
            this.checkAndScheduleFetch();
            this.checkSemaphore -= 1;
        }, DEFAULT_FETCH_CHECK_INTERVAL_MS);
    }

    // Howler tries to load the whole fricking buffer when using WebAudio, which makes the tab crash on large sounds.
    // I'm sure there's a better solution, but it probably involves using the WebAudio API directly.
    // Ref: https://github.com/bytebardsoftware/bardic.tools/issues/115
    async shouldUseWebAudio(sound: Sound): Promise<boolean> {
        console.debug(`BL: shouldUseWebAudio(${sound.name})`)
        if (!this.isSoundInCache(sound)) {
            return false;
        }

        const blob = await this.readSound(sound);
        if (blob === undefined) {
            return false;
        }

        return blob.size < DEFAULT_WEB_AUDIO_MAX_BLOB_SIZE;
    }

    async isSoundInCache(sound: Sound): Promise<boolean> {
        console.debug(`BL: isSoundInCache(${sound.name})`)
        for (const readLayer of this.configuration.read) {
            switch (readLayer) {
                case SoundCacheLayer.PASSTHROUGH:
                    break
                case SoundCacheLayer.MEMORY:
                    if (this.inMemorySounds.has(sound.id)) {
                        console.debug(`BL: Sound ${sound.name} is in MEMORY cache`);
                        return true;
                    };
                case SoundCacheLayer.IDB:
                    {
                        const match = await this.db.getKey(STORE_NAME, sound.id)
                        if (match !== undefined) {
                            console.debug(`BL: Sound ${sound.name} is in IDB cache`);
                            return true;
                        };
                    }
                // case SoundCacheLayer.FS_API:
                //     const fileHandle = await this.getFsHandle(sound, false);
                //     if (fileHandle !== undefined) {
                //         return true;
                //     }
                //     break;
            }
        }
        // None of the layers returned true
        return false;
    }

    async readSound(sound: Sound): Promise<Blob | undefined> {
        const id = sound.id;
        console.debug(`BL: readSound(${sound.name})`)
        for (const readLayer of this.configuration.read) {
            switch (readLayer) {
                case SoundCacheLayer.MEMORY:
                    if (this.inMemorySounds.has(id)) {
                        const cachedBlob = this.inMemorySounds.get(id);
                        if (cachedBlob !== undefined) {
                            console.debug(`BL: Returning result from the MEMORY cache ${sound.name}`)
                            return cachedBlob;
                        }
                    }
                    break;
                case SoundCacheLayer.IDB: {
                    const match = await this.db.getKey(STORE_NAME, sound.id)
                    if (match) {
                        const object = await this.db.get(STORE_NAME, sound.id)
                        // this.inMemorySounds.set(sound.id, object.blob)
                        console.debug("BL: Returning result from the IDB cache" + sound.name)
                        return object.blob;
                    }
                    break;
                }
                // case SoundCacheLayer.FS_API:
                //     const fileHandle = await this.getFsHandle(sound, false);
                //     if (fileHandle !== undefined) {
                //         const file = await fileHandle.getFile();
                //         return file;
                //     }
                //     break;
            }
        }
        // None of the read layers had it.
        return undefined;
    }

    // async getFsHandle(sound: Sound, create: boolean): Promise<FileSystemFileHandle | undefined> {
    //     const directoryHandle = await this.fsRoot.getDirectoryHandle("sounds", { create: true });
    //     try {
    //         return await directoryHandle.getFileHandle(sound.id, { create: create });
    //     } catch (e) {
    //         if (e instanceof DOMException && e.name === "NotFoundError") {
    //             return undefined; // We may want to return something different here.
    //         } else {
    //             throw e;
    //         }
    //     }
    // }

    async writeSound(sound: Sound, content: Blob) {
        console.debug(`BL: writeSound(${sound.name})`)
        if (content.size > this.configuration.maxBlobSize) {
            console.debug(`BL: refusing to write big sound ${sound.name} in cache. Size is ${content.size}, max size is ${this.configuration.maxBlobSize}`)
            return;
        }
        for (const writeLayer of this.configuration.write) {
            switch (writeLayer) {
                case SoundCacheLayer.PASSTHROUGH:
                    break;
                case SoundCacheLayer.MEMORY:
                    this.inMemorySounds.set(sound.id, content);
                    break;
                case SoundCacheLayer.IDB:
                    {
                        this.db.put(STORE_NAME, {
                            id: sound.id,
                            blob: content,
                            name: sound.name,
                            html_data_type: sound.html_data_type,
                        })
                    }
                    break;
                // case SoundCacheLayer.FS_API:
                //     const fileHandle = await this.getFsHandle(sound, true);
                //     const writer = await fileHandle.createWritable();
                //     await writer.write(content);
                //     await writer.close();
                //     break;
            }
        }
    }

    checkAndScheduleFetch() {
        if (this.fetchQueue.length === 0) {
            return;
        }
        if (this.fetchSemaphore < this.configuration.maxParallelFetches) {
            const slots = this.configuration.maxParallelFetches - this.fetchSemaphore;
            for (let i = 0; i < slots && this.fetchQueue.length > 0; i++) {
                const sound = this.fetchQueue.pop();
                this.doFetch(sound);
            }
        }
    }

    doFetch(sound: Sound) {
        const url = sound.datapath;

        this.fetchSemaphore += 1;
        fetch(url)
            .then((resp) => resp.blob())
            .then((body) => {
                console.debug(`BL: Sound fetched for url ${url} with body ${body}`)
                this.writeSound(sound, body)
            })
            .catch((err) => console.warn(`Fetch failed for url ${url}: ${err}`))
            .finally(() => this.fetchSemaphore -= 1);
    }

    enqueueFetch(sound: Sound) {
        this.fetchQueue.push(sound);
    }

    async fetchSound(sound: Sound): Promise<string> {
        const url = sound.datapath;
        console.debug(`BL: fetchSound(${sound.name})`)

        try {
            const cachedBlob: Blob | undefined = await this.readSound(sound);
            if (cachedBlob !== undefined) {
                return URL.createObjectURL(cachedBlob)
            }
        } catch (e) {
            console.error(`Error reading from cache: ${e}. Falling back to passthrough.`)
        }

        console.debug(`BL: Didn't get db hit, fetching from the network and returning unchanged datapath...`)
        if (sound.size_bytes !== undefined && sound.size_bytes > this.configuration.maxBlobSize) {
            console.debug(`BL: refusing to fetch big sound ${sound.name} in cache. Size is ${sound.size_bytes}, max size is ${this.configuration.maxBlobSize}`)
            return url;
        }

        // Configuration is supposed to be immutable after init, so we don't even bother re-checking this when we go to fetch.
        if (this.configuration.write.length === 0) {
            console.debug(`BL: Refusing to fetch sound ${sound.name} to cache, as there is nowhere to write to`);
            return url;
        }

        this.enqueueFetch(sound);
        console.debug(`BL: Returning url after enqueueing ${url}`);
        return url;
    }
}

let soundCachePromise = null;
export async function getOrCreateSoundCache(): Promise<SoundCache> {
    if (soundCachePromise === null) {
        soundCachePromise = Promise.all([
            openDB(DB_NAME, DB_VERSION, {
                upgrade: (db) => {
                    // Create an objectStore to store our videos in (basically like a single table)
                    // including a auto-incrementing key
                    const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

                    // Define what data items the objectStore will contain
                    objectStore.createIndex('name', 'name', { unique: false });
                    objectStore.createIndex('blob', 'blob', { unique: false });
                    objectStore.createIndex('html_datatype', 'html_data_type', { unique: false });
                }
            }),
            // navigator.storage.getDirectory(),
        // ]).then(([db, fsRoot]) => {
        ]).then(([db]) => {
            // return new SoundCache(db, fsRoot);
            return new SoundCache(db);
        });
    }
    return await soundCachePromise;
}